import { useState } from 'react';
import { ChevronLeft, ChevronRight, Megaphone, CheckCircle2, AlertCircle } from 'lucide-react';
import { Button, cn } from '@jeraisonne/ui';
import { type DiffusionRow, isActionable } from '@jeraisonne/db/diffusions';
import {
  useDiffusions,
  useBulkDiffusions,
  DIFFUSIONS_PAGE_SIZE,
  type DiffusionsFilters,
  type BulkDiffusionsResult,
} from '@/lib/admin/diffusions-queries';
import { DiffusionsCreateBar } from './diffusions-create-bar';
import { DiffusionsFilterBar } from './diffusions-filter-bar';
import { DiffusionRowItem, DiffusionsTableHeader } from './diffusion-row';
import { DiffusionsBulkBar } from './diffusions-bulk-bar';
import { ScheduleModal } from './schedule-modal';

export function DiffusionsList() {
  const [filters, setFilters] = useState<DiffusionsFilters>({});
  const [page, setPage] = useState(1);
  // Sélection conservée à travers les pages (id → ligne complète).
  const [selected, setSelected] = useState<Map<string, DiffusionRow>>(new Map());
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [report, setReport] = useState<BulkDiffusionsResult | null>(null);

  const { data, isLoading } = useDiffusions({ filters, page });
  const bulk = useBulkDiffusions();

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / DIFFUSIONS_PAGE_SIZE));

  const selectableOnPage = rows.filter((r) => isActionable(r.status));
  const allPageSelected =
    selectableOnPage.length > 0 && selectableOnPage.every((r) => selected.has(r.id));

  function toggleRow(row: DiffusionRow) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.id)) next.delete(row.id);
      else next.set(row.id, row);
      return next;
    });
  }

  function togglePage() {
    setSelected((prev) => {
      const next = new Map(prev);
      if (allPageSelected) selectableOnPage.forEach((r) => next.delete(r.id));
      else selectableOnPage.forEach((r) => next.set(r.id, r));
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Map());
  }

  function updateFilters(next: DiffusionsFilters) {
    setFilters(next);
    setPage(1);
  }

  const selectedRows = Array.from(selected.values());
  const targets = selectedRows.map((r) => ({
    kind: r.kind,
    source_id: r.source_id,
    platform: r.platform,
  }));
  const needsCarousel = selectedRows.some(
    (r) => r.kind === 'blog' && (r.platform === 'instagram' || r.platform === 'linkedin'),
  );

  async function runBulk(action: 'publish_now' | 'schedule', scheduledAt?: string) {
    setReport(null);
    const res = await bulk.mutateAsync({ targets, action, scheduled_at: scheduledAt });
    setReport(res);
    setScheduleOpen(false);
    clearSelection();
  }

  function onPublishNow() {
    if (
      !window.confirm(
        `Publier maintenant ${targets.length} diffusion${targets.length > 1 ? 's' : ''} ?`,
      )
    )
      return;
    void runBulk('publish_now');
  }

  return (
    <div className={cn(selected.size > 0 && 'pb-28')}>
      <DiffusionsCreateBar />
      <DiffusionsFilterBar filters={filters} onChange={updateFilters} />

      {report && (
        <div className="border-ink-200 mb-4 flex items-start gap-2 rounded-2xl border bg-white p-3">
          {report.failed === 0 ? (
            <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
          ) : (
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          )}
          <div className="min-w-0 flex-1 font-sans text-xs">
            <p className="text-ink-800 font-semibold">
              {report.scheduled > 0 && `${report.scheduled} programmée(s). `}
              {report.launched > 0 && `${report.launched} lancée(s). `}
              {report.failed > 0 && `${report.failed} en échec.`}
              {report.scheduled === 0 &&
                report.launched === 0 &&
                report.failed === 0 &&
                'Aucune action.'}
            </p>
            {report.results
              .filter((r) => !r.ok)
              .slice(0, 4)
              .map((r, i) => (
                <p key={i} className="text-red-500">
                  {r.platform} — {r.error}
                </p>
              ))}
          </div>
          <button
            onClick={() => setReport(null)}
            className="text-ink-400 hover:text-ink-700 text-xs"
          >
            Fermer
          </button>
        </div>
      )}

      {!isLoading && rows.length > 0 && (
        <p className="text-ink-500 mb-2 px-3 font-sans text-xs">
          {total} diffusion{total > 1 ? 's' : ''}
          {selected.size > 0 && ` · ${selected.size} sélectionnée${selected.size > 1 ? 's' : ''}`}
        </p>
      )}

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-ink-100 h-20 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="border-ink-200 flex flex-col items-center rounded-2xl border border-dashed bg-white p-10 text-center">
          <Megaphone className="text-ink-300 h-8 w-8" />
          <p className="font-display text-ink-700 mt-3 text-sm font-bold">Aucune diffusion</p>
          <p className="text-ink-500 mt-1 font-sans text-xs">
            Crée une vidéo, diffuse un défi ou publie un article pour les retrouver ici.
          </p>
        </div>
      ) : (
        <div>
          <DiffusionsTableHeader
            allSelected={allPageSelected}
            selectableCount={selectableOnPage.length}
            onToggleAll={togglePage}
          />
          <div className="space-y-2.5">
            {rows.map((row) => (
              <DiffusionRowItem
                key={row.id}
                row={row}
                selected={selected.has(row.id)}
                onToggle={toggleRow}
              />
            ))}
          </div>
        </div>
      )}

      {pageCount > 1 && (
        <div className="mt-4 flex items-center justify-between gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" /> Précédent
          </Button>
          <span className="text-ink-500 font-sans text-xs">
            Page {Math.min(page, pageCount)} / {pageCount}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= pageCount}
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
          >
            Suivant <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}

      {selected.size > 0 && (
        <DiffusionsBulkBar
          count={selected.size}
          pending={bulk.isPending}
          onPublishNow={onPublishNow}
          onSchedule={() => setScheduleOpen(true)}
          onClear={clearSelection}
        />
      )}

      {scheduleOpen && (
        <ScheduleModal
          count={selected.size}
          needsCarousel={needsCarousel}
          pending={bulk.isPending}
          onClose={() => setScheduleOpen(false)}
          onConfirm={(iso) => void runBulk('schedule', iso)}
        />
      )}
    </div>
  );
}
